/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import api from "../api";
import { Query_Keys } from "../lib/queryKeys";
import { PerPage } from "../constants/perPage";
import { getDropdownNextPageParam, mergeOptionsIfChanged } from "../utils/dropdownInfiniteQuery";
import { useInfiniteScrollObserver } from "./useInfiniteScrollObserver";
import { useDebounce } from "../utils/useDebounce";
import {
  ALL_AUDIENCE_MODE,
  AUDIENCE_PREVIEW_ENTITY_TYPES,
  CARD_AUDIENCE_MODE,
  JOB_ROLE_AUDIENCE_MODE,
  mapPrefillToPartnerProgram,
  mergePrefillContract,
  normalizeAudienceTitle,
  parseEntityKey,
  RECIPIENT_POLICY_LAYER,
  recipientScope,
  RESTAURANT_AUDIENCE_MODE,
} from "../components/policy_engine_modal/partner_program_configuration";

// Stable defaults for queries that stay disabled until the modal opens and a
// recipient is chosen: `data` is `undefined` on every render until then, and an
// inline `[]` would hand the option-cache effects a fresh array each time.
const EMPTY_OPTIONS = [];

const DROPDOWN_STALE_TIME = 10 * 60 * 1000;
const SEARCH_DEBOUNCE_MS = 300;
// The preview authorizes one PDP call per selected value, so composing a selection
// pick by pick costs far more than a list fetch. Debounced like the scope check.
const AUDIENCE_PREVIEW_DEBOUNCE_MS = 300;
// Without it RestaurantsList demands a per-restaurant grant; with it the parent
// group's grant is accepted, which is how a group-level admin sees its own estate.
const AUDIENCE_RESOURCE_ACTION = "create";
const SEARCH_RESET_DELAY_MS = 1000;

// The check and the prefill are both answers about live state — an active policy
// occupying the pair's slot, and the terms currently in force — so neither is cached.
const UNCACHED_QUERY_OPTIONS = {
  staleTime: 0,
  gcTime: 0,
  refetchOnMount: "always",
  retry: false,
};

/**
 * @description A searchable, infinitely scrolling dropdown backed by one endpoint.
 * Returns the bundle the modal's dropdown props take, plus the search setter the
 * caller needs for nothing else — the bundle already carries it.
 * @param config Query key, fetcher, and whether the query may run.
 * @returns The dropdown prop bundle.
 */
// Applied in `select`, never in the fetcher: getDropdownNextPageParam decides there is
// a next page from the raw page length, so shrinking a page there ends pagination early.
const dedupeOptions = (options, optionKey) => {
  const unique = new Map();

  options.forEach((option) => {
    const key = option?.[optionKey];

    if (key && !unique.has(key)) {
      unique.set(key, option);
    }
  });

  return [...unique.values()];
};

const useDropdown = ({ queryKey, fetchPage, enabled, optionKey = "id", dedupe = false }) => {
  const [search, setSearch] = useState("");
  const [optionsCache, setOptionsCache] = useState([]);
  const searchResetTimeoutRef = useRef(null);
  const debouncedSearch = useDebounce(search.trim(), SEARCH_DEBOUNCE_MS);

  const {
    data: options = EMPTY_OPTIONS,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: queryKey(debouncedSearch),
    queryFn: ({ pageParam = 1 }) => fetchPage({ pageParam, search: debouncedSearch || undefined }),
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => (dedupe ? dedupeOptions(data.pages.flat(), optionKey) : data.pages.flat()),
    staleTime: DROPDOWN_STALE_TIME,
    placeholderData: keepPreviousData,
    enabled,
  });

  const { rootRef: menuRef, targetRef: loaderRef } = useInfiniteScrollObserver({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  });

  // mergeOptionsIfChanged, not mergeUniqueByKey: it returns the current array
  // reference when the merge is a no-op, so a re-run cannot re-trigger a render.
  useEffect(() => {
    setOptionsCache((currentOptions) => (
      mergeOptionsIfChanged(currentOptions, options, optionKey, ["name", "label", "relationship_name"])
    ));
  }, [options, optionKey]);

  // The search term is kept for a beat after the menu closes so the rendered
  // selection does not flicker while the reset query settles.
  const onMenuClose = () => {
    if (searchResetTimeoutRef.current) {
      window.clearTimeout(searchResetTimeoutRef.current);
    }

    searchResetTimeoutRef.current = window.setTimeout(() => {
      setSearch("");
      searchResetTimeoutRef.current = null;
    }, SEARCH_RESET_DELAY_MS);
  };

  useEffect(() => () => {
    if (searchResetTimeoutRef.current) {
      window.clearTimeout(searchResetTimeoutRef.current);
    }
  }, []);

  return {
    options,
    optionsCache,
    isFetching,
    hasNextPage,
    menuRef,
    loaderRef,
    onSearchChange: setSearch,
    onMenuClose,
  };
};

/**
 * @description Every query the Partner Benefit Program form reads from: the
 * partnership list, the recipient-scoped selectors, the active-policy check on the
 * directed pair, the recipient's prefill contract and the live audience count.
 *
 * Kept out of the page because the program form's data needs are its own — the page
 * only forwards the bundles to the modal.
 * @param options { enabled, isDraft, isEditMode, partnerProgramData, setPartnerProgramData }.
 * @returns The modal's partner-program props.
 */
export const usePartnerProgramData = ({
  enabled = false,
  isDraft = false,
  isEditMode = false,
  partnerProgramData,
  setPartnerProgramData,
}) => {
  const policyLayer = partnerProgramData?.policy_layer || "";
  const isRecipientLayer = policyLayer === RECIPIENT_POLICY_LAYER;
  const provider = parseEntityKey(partnerProgramData?.provider_key);
  const recipient = parseEntityKey(partnerProgramData?.recipient_key);
  const hasProgramSides = Boolean(provider && recipient);
  const { restaurantGroupId, restaurantId } = recipientScope(partnerProgramData);
  const hasRecipientScope = Boolean(restaurantGroupId || restaurantId);

  const partnershipDropdown = useDropdown({
    queryKey: (search) => Query_Keys.active_partners_dropdown({ search }),
    fetchPage: async ({ pageParam, search }) => {
      const response = await api.get("/v1/admin/dropdowns/active_partners_list", {
        params: { page: pageParam, per_page: PerPage, search },
      });
      return response?.data || [];
    },
    // An edit does not render the partnership field: the pair is the program's
    // identity, and its two sides come off the policy itself.
    enabled: enabled && !isEditMode,
    optionKey: "id",
  });

  // Both card lists are the recipient's own cards: the offers ride on cards the
  // recipient's people already hold, and the card-level audience is drawn from the
  // same estate. Two queries rather than one so the two fields search independently.
  const recipientCardParams = useMemo(() => ({
    restaurant_group_id: restaurantGroupId || undefined,
    restaurant_id: restaurantId || undefined,
  }), [restaurantGroupId, restaurantId]);

  const fetchRecipientCards = async ({ pageParam, search }) => {
    const response = await api.get("/v1/admin/dropdowns/discount_card_list", {
      params: { ...recipientCardParams, page: pageParam, per_page: PerPage, search },
    });
    return response?.data?.map((card) => ({ id: card?.value, name: card?.label })) || [];
  };

  const benefitCardsDropdown = useDropdown({
    queryKey: (search) => Query_Keys.discount_cards_dropdown({ ...recipientCardParams, search, source: "benefit" }),
    fetchPage: fetchRecipientCards,
    enabled: enabled && hasRecipientScope,
  });

  const audienceCardsDropdown = useDropdown({
    queryKey: (search) => Query_Keys.discount_cards_dropdown({ ...recipientCardParams, search, source: "audience" }),
    fetchPage: fetchRecipientCards,
    enabled: enabled && hasRecipientScope && partnerProgramData?.audience_mode === CARD_AUDIENCE_MODE,
  });

  const programExceptionUsersDropdown = useDropdown({
    queryKey: (search) => Query_Keys.active_guests_list({
      restaurant_group_id: restaurantGroupId,
      restaurant_id: restaurantId,
      search,
      source: "policy_engine_program",
    }),
    fetchPage: async ({ pageParam, search }) => {
      const response = await api.get("/v1/admin/dropdowns/active_guests_list", {
        params: {
          restaurant_group_id: restaurantGroupId || undefined,
          restaurant_id: restaurantId ? [restaurantId] : undefined,
          page: pageParam,
          per_page: PerPage,
          search,
        },
      });
      return response?.data?.map((guest) => ({ id: guest?.value, name: guest?.label })) || [];
    },
    enabled: enabled && hasRecipientScope,
  });

  // A group recipient brings its whole estate to choose from. A restaurant recipient is
  // the only restaurant its own audience can name (AudienceValidatable#permitted_restaurant_ids),
  // so the list is not queried at all — that one option is substituted below.
  const audienceRestaurantsDropdown = useDropdown({
    queryKey: (search) => Query_Keys.restaurants_dropdown({
      restaurant_group_ids: restaurantGroupId,
      resource_action: AUDIENCE_RESOURCE_ACTION,
      search,
      source: "policy_engine_audience",
    }),
    fetchPage: async ({ pageParam, search }) => {
      const response = await api.get("/v1/admin/dropdowns/restaurants_list", {
        params: {
          restaurant_group_ids: restaurantGroupId ? [restaurantGroupId] : undefined,
          resource_action: AUDIENCE_RESOURCE_ACTION,
          page: pageParam,
          per_page: PerPage,
          search,
        },
      });
      return response?.data?.map((restaurant) => ({ id: restaurant?.value, name: restaurant?.label })) || [];
    },
    enabled: enabled && Boolean(restaurantGroupId)
      && partnerProgramData?.audience_mode === RESTAURANT_AUDIENCE_MODE,
  });

  // `job_role_level` selects TITLE strings, not job ids, so the title is the option's
  // value — and the same title at several restaurants is one choice, hence the dedupe.
  const audienceJobsDropdown = useDropdown({
    queryKey: (search) => Query_Keys.jobs_dropdown({
      restaurant_group_id: restaurantGroupId,
      restaurant_ids: restaurantId,
      search,
      source: "policy_engine_audience",
    }),
    fetchPage: async ({ pageParam, search }) => {
      const response = await api.get("/v1/admin/dropdowns/jobs_list", {
        params: {
          restaurant_group_id: restaurantGroupId || undefined,
          restaurant_ids: restaurantId ? [restaurantId] : undefined,
          page: pageParam,
          per_page: PerPage,
          search,
        },
      });
      // One entry per API row so the page length stays the endpoint's; `dedupe`
      // collapses the repeats across pages, and a blank title carries no key.
      // Keyed by the stored (normalized) form so a saved selection matches on edit.
      return (response?.data || []).map((job) => ({
        id: normalizeAudienceTitle(job?.title),
        name: `${job?.title || ""}`.trim(),
      }));
    },
    enabled: enabled && hasRecipientScope && partnerProgramData?.audience_mode === JOB_ROLE_AUDIENCE_MODE,
    dedupe: true,
  });

  // A restaurant recipient is never offered a restaurant list, so its own name has to
  // come off the partnership row or the selection would render as a bare id.
  const pinnedRecipientOption = useMemo(() => {
    if (!restaurantId) {
      return null;
    }

    const sides = [partnerProgramData?.partnership?.entity_one, partnerProgramData?.partnership?.entity_two];
    const side = sides.find((entity) => entity?.entity_id === restaurantId);

    return { id: restaurantId, name: side?.entity_name || `${restaurantId}` };
  }, [restaurantId, partnerProgramData?.partnership]);

  const resolvedAudienceRestaurants = useMemo(() => (
    pinnedRecipientOption
      ? { ...audienceRestaurantsDropdown, options: [pinnedRecipientOption], optionsCache: [pinnedRecipientOption] }
      : audienceRestaurantsDropdown
  ), [pinnedRecipientOption, audienceRestaurantsDropdown]);

  const programPairParams = useMemo(() => (
    hasProgramSides
      ? {
        partnership_provider_type: provider.type,
        partnership_provider_id: provider.id,
        partnership_recipient_type: recipient.type,
        partnership_recipient_id: recipient.id,
      }
      : null
  ), [hasProgramSides, provider?.type, provider?.id, recipient?.type, recipient?.id]);

  // A draft occupies no scope, so it has nothing to check against — and an edit's
  // pair is already held by the very policy being edited, so the check would report a
  // 422 against itself.
  const checkEnabled = enabled && Boolean(programPairParams) && Boolean(policyLayer)
    && !isDraft && !isEditMode;

  const {
    isFetching: programCheckFetching,
    isPending: programCheckPending,
    isSuccess: programCheckPassed,
    error: programCheckError,
  } = useQuery({
    queryKey: Query_Keys.check_active_policy({ ...programPairParams, policy_type: policyLayer }),
    queryFn: async () => {
      const response = await api.get("/v1/admin/policy_engines/check_active_policy", {
        params: { ...programPairParams, policy_type: policyLayer, resource_action: "create" },
      });
      return response?.data;
    },
    enabled: checkEnabled,
    ...UNCACHED_QUERY_OPTIONS,
  });

  // Built off the endpoint's contract, never re-derived in JS: the bounds it reports
  // are the ones OverrideCompliant enforces, and a second implementation would drift.
  const prefillEnabled = enabled && isRecipientLayer && Boolean(programPairParams);

  const {
    data: recipientPrefill,
    isFetching: prefillFetching,
    error: prefillError,
  } = useQuery({
    queryKey: Query_Keys.recipient_prefill(programPairParams || {}),
    queryFn: async () => {
      const response = await api.get("/v1/admin/policy_engines/recipient_prefill", { params: programPairParams });
      return response?.data;
    },
    enabled: prefillEnabled,
    ...UNCACHED_QUERY_OPTIONS,
  });

  // Seeded once per contract: the recipient edits the values afterwards, so re-running
  // on every render would overwrite what they just typed.
  const seededPrefillIdRef = useRef(null);

  useEffect(() => {
    if (!isRecipientLayer || !recipientPrefill) {
      return;
    }

    const prefillId = `${recipientPrefill?.provider_policy?.id}`;

    if (seededPrefillIdRef.current === prefillId) {
      return;
    }

    seededPrefillIdRef.current = prefillId;
    // An edit takes the contract only: the values are already the policy's own, and
    // the provider's defaults would overwrite what the recipient authored.
    setPartnerProgramData((currentData) => (
      isEditMode
        ? mergePrefillContract(recipientPrefill, currentData)
        : mapPrefillToPartnerProgram(recipientPrefill, currentData)
    ));
  }, [isEditMode, isRecipientLayer, recipientPrefill, setPartnerProgramData]);

  // Any layer or pair move forgets the seeded contract — returning to a pair already
  // seeded must re-seed too, or its refetch is ignored and the form stays frozen.
  useEffect(() => {
    seededPrefillIdRef.current = null;
  }, [isRecipientLayer, programPairParams]);

  const audiencePreviewParams = useMemo(() => {
    const mode = partnerProgramData?.audience_mode || ALL_AUDIENCE_MODE;
    // The recipient's own estate — what `all` reaches, and so the bound every other
    // mode narrows. A restaurant recipient IS one restaurant; a group brings all of its.
    const scope = restaurantId ? { restaurant_id: restaurantId } : { restaurant_group_id: restaurantGroupId };

    if (!restaurantId && !restaurantGroupId) {
      return null;
    }

    // `all` names no selection, so it resolves against the recipient itself.
    if (mode === ALL_AUDIENCE_MODE) {
      return restaurantId
        ? { entity_type: "restaurant", values: [restaurantId] }
        : { entity_type: "restaurant_group", values: [restaurantGroupId] };
    }

    const values = partnerProgramData?.audience_values || [];

    if (!values.length) {
      return null;
    }

    // job_role names titles and card_level names group-owned cards, so neither is
    // bounded by its own selection — the recipient's estate is what scopes the lookup,
    // and what its authorization is checked against.
    if (mode === JOB_ROLE_AUDIENCE_MODE || mode === CARD_AUDIENCE_MODE) {
      return { entity_type: AUDIENCE_PREVIEW_ENTITY_TYPES[mode], values, ...scope };
    }

    return { entity_type: AUDIENCE_PREVIEW_ENTITY_TYPES[mode], values };
  }, [
    partnerProgramData?.audience_mode,
    partnerProgramData?.audience_values,
    restaurantGroupId,
    restaurantId,
  ]);

  const debouncedAudiencePreviewParams = useDebounce(audiencePreviewParams, AUDIENCE_PREVIEW_DEBOUNCE_MS);
  // A pair still being validated has no audience worth counting, and the check is the
  // cheaper of the two requests to spend first.
  const audiencePreviewEnabled = enabled
    && Boolean(debouncedAudiencePreviewParams)
    && (!checkEnabled || programCheckPassed);

  const { data: audiencePreviewData, isFetching: audiencePreviewFetching } = useQuery({
    queryKey: Query_Keys.audience_preview(debouncedAudiencePreviewParams || {}),
    queryFn: async () => {
      const response = await api.get("/v1/admin/policy_engines/audience_preview", {
        params: debouncedAudiencePreviewParams,
      });
      return response?.data;
    },
    enabled: audiencePreviewEnabled,
    staleTime: 0,
    retry: false,
    placeholderData: keepPreviousData,
  });

  return {
    partnershipDropdown,
    benefitCardsDropdown,
    audienceCardsDropdown,
    programExceptionUsersDropdown,
    audienceRestaurantsDropdown: resolvedAudienceRestaurants,
    audienceJobsDropdown,
    partnerProgramCheck: {
      isChecking: checkEnabled && (programCheckFetching || programCheckPending),
      isClear: checkEnabled && !programCheckFetching && programCheckPassed,
      error: programCheckError,
    },
    partnerProgramPrefill: {
      isFetching: prefillFetching,
      // The 404 the endpoint answers with when no active provider policy governs the
      // pair — the same precondition the recipient create is refused on.
      isMissing: prefillError?.response?.status === 404,
      error: prefillError,
    },
    audiencePreview: {
      count: audiencePreviewParams ? (audiencePreviewData?.count ?? 0) : 0,
      // Pending through the debounce too, so the count never reads as settled while
      // it still answers the previous selection.
      isFetching: audiencePreviewFetching || audiencePreviewParams !== debouncedAudiencePreviewParams,
    },
  };
};
